import React, { useEffect, useState } from 'react';
import http from 'services/http';
import { alerta } from 'components/Alert/index';
import formatMoney from 'utils/formatMoney';
import PermissionGate from 'utils/PermissionGate';
import Pagination from 'components/Paginacao';
import 'components/Paginacao/style.css';
import ModalBilhete from './ModalBilhete';

export default function Raspadinha() {
    const [modelos, setModelos] = useState([]);
    const [raspadinhas, setRaspadinhas] = useState([]);
    const [criando, setCriando] = useState(null);
    const [cancelando, setCancelando] = useState(null);
    const [telaAtual, setTelaAtual] = useState('modelos');
    const [raspadinhaSelecionada, setRaspadinhaSelecionada] = useState(null);
    const [bilhetes, setBilhetes] = useState([]);
    const [loadingBilhetes, setLoadingBilhetes] = useState(false);
    const [modalBilhete, setModalBilhete] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalBilhetes, setTotalBilhetes] = useState(0);
    const pageSize = 50; // mesmo valor do backend (paginate(50))

    useEffect(() => {
        buscar();
    }, []);

    const buscar = async () => {
        try {
            const { data: { data } } = await http.get('/raspadinhas');
            setModelos(data.modelos ?? []);
            setRaspadinhas(data.raspadinhas ?? []);
        } catch (e) {
            alerta(e.response.data);
        }
    };

    const criar = async (modeloId) => {
        setCriando(modeloId);
        try {
            const response = await http.post('/raspadinhas', { modelo_id: modeloId });
            alerta(response.data);
            buscar();
        } catch (e) {
            alerta(e.response.data);
        } finally {
            setCriando(null);
        }
    };

    const cancelar = async (id) => {
        if (!window.confirm('Deseja cancelar esta raspadinha?')) return;
        setCancelando(id);
        try {
            const response = await http.delete(`/raspadinhas/${id}`);
            alerta(response.data);
            buscar();
        } catch (e) {
            alerta(e.response.data);
        } finally {
            setCancelando(null);
        }
    };

    const abrirBilhetes = (raspadinha) => {
        setRaspadinhaSelecionada(raspadinha);
        setTelaAtual('bilhetes');
        setBilhetes([]);
        setTotalBilhetes(0);
        setCurrentPage(1);
        // O carregamento é disparado pelo useEffect abaixo (depende de raspadinha + página).
    };

    // Carrega os bilhetes (paginado) sempre que a raspadinha selecionada ou a página mudar.
    useEffect(() => {
        if (telaAtual !== 'bilhetes' || !raspadinhaSelecionada) return;

        let ativo = true;
        const carregar = async () => {
            setLoadingBilhetes(true);
            try {
                const { data: resposta } = await http.get(
                    `/raspadinhas/${raspadinhaSelecionada.id}/bilhetes`,
                    { params: { page: currentPage } }
                );

                if (!ativo) return;

                const paginator = resposta.data ?? {};
                setBilhetes(paginator.data ?? []);
                setTotalBilhetes(paginator.total ?? 0);
            } catch (e) {
                if (ativo) alerta(e.response.data);
            } finally {
                if (ativo) setLoadingBilhetes(false);
            }
        };

        carregar();
        return () => { ativo = false; };
    }, [telaAtual, raspadinhaSelecionada, currentPage]);

    const mudarPagina = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const pagarPremio = async (bilheteId) => {
        try {
            const response = await http.patch(`/raspadinhas/bilhetes/id/${bilheteId}/pagar`);
            alerta(response.data);
            setBilhetes(prev => prev.map(b => b.id === bilheteId ? { ...b, situacao: 'pago' } : b));
            setModalBilhete(prev => {
                if (!prev) return prev;
                if (prev.id === bilheteId) return { ...prev, situacao: 'pago' };
                if (prev.bilhetes?.some(b => b.id === bilheteId)) {
                    return {
                        ...prev,
                        bilhetes: prev.bilhetes.map(b =>
                            b.id === bilheteId ? { ...b, situacao: 'pago' } : b
                        ),
                    };
                }
                return prev;
            });
        } catch (e) {
            alerta(e.response.data);
        }
    };

    const grupos = [
        { label: 'R$ 2,00', valor: 2 },
        { label: 'R$ 5,00', valor: 5 },
        { label: 'R$ 10,00', valor: 10 },
    ];

    // tela de bilhetes
    if (telaAtual === 'bilhetes') {
        return (
            <>
                <ModalBilhete
                    bilhete={modalBilhete}
                    onClose={() => setModalBilhete(null)}
                    onPagar={pagarPremio}
                    autoRevelar
                />
                <div className="titulo-principal d-flex align-items-center">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mr-3"
                        onClick={() => { setTelaAtual('modelos'); setRaspadinhaSelecionada(null); setBilhetes([]); setTotalBilhetes(0); setCurrentPage(1); }}
                    >
                        ← Voltar
                    </button>
                    Bilhetes — {raspadinhaSelecionada?.nome}
                </div>

                <div className="conteudo-principal p-0">
                    {loadingBilhetes ? (
                        <div className="p-4 text-center">Carregando...</div>
                    ) : (
                        <table className="table table-sm text-center border mb-0">
                            <thead className="thead-light">
                                <tr>
                                    <th>Código</th>
                                    <th>Operador</th>
                                    <th>Gerente</th>
                                    <th>Etapa</th>
                                    <th>Faixa</th>
                                    <th>Valor</th>
                                    <th>Prêmio</th>
                                    <th>Situação</th>
                                    <th>Data</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {bilhetes.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-muted p-4">Nenhum bilhete gerado ainda.</td>
                                    </tr>
                                ) : bilhetes.map(b => (
                                    <tr key={b.id}>
                                        <td><code>{b.codigo}</code></td>
                                        <td>{b.operador?.nome ?? '-'}</td>
                                        <td>{b.gerente?.nome ?? '-'}</td>
                                        <td>{b.etapa}ª</td>
                                        <td>{b.faixa?.nome ?? 'Perdedor'}</td>
                                        <td>{formatMoney(b.valor)}</td>
                                        <td>{formatMoney(b.premio)}</td>
                                        <td>
                                            <span className={`badge badge-${
                                                b.situacao === 'pago' ? 'success' :
                                                b.situacao === 'gerado' && b.premio > 0 ? 'warning' :
                                                'secondary'
                                            }`}>
                                                {b.situacao}
                                            </span>
                                        </td>
                                        <td>{new Date(b.created_at).toLocaleString('pt-BR')}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-info mr-1"
                                                onClick={() => setModalBilhete(b)}
                                            >
                                                Ver
                                            </button>
                                            {b.situacao === 'gerado' && b.premio > 0 && (
                                                <PermissionGate permissions={['Criar raspadinha']}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => pagarPremio(b.id)}
                                                    >
                                                        Pagar
                                                    </button>
                                                </PermissionGate>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {!loadingBilhetes && totalBilhetes > pageSize && (
                        <div className="d-flex justify-content-center py-2">
                            <Pagination
                                currentPage={currentPage}
                                totalCount={totalBilhetes}
                                pageSize={pageSize}
                                onPageChange={mudarPagina}
                            />
                        </div>
                    )}
                </div>
            </>
        );
    }

    return (
        <>
            <div className="titulo-principal">Raspadinha</div>

            {/* Modelos disponíveis para criar */}
            {grupos.map(grupo => {
                const lista = modelos.filter(m => Number(m.valor) === grupo.valor);
                // Tem raspadinha ativa deste valor?
                const temAtiva = raspadinhas.some(r => Number(r.valor) === grupo.valor && r.situacao === 'ativa');

                return (
                    <div key={grupo.valor} className="conteudo-principal mb-3">
                        <h5 className="mb-3 font-weight-bold">Modelos {grupo.label}</h5>
                        <div className="row m-0">
                            {lista.map(modelo => {
                                const emUso = modelo.raspadinha !== null;
                                return (
                                    <div key={modelo.id} className="col-md-4 px-1 mb-3">
                                        <div className={`card h-100 border ${emUso ? 'border-success' : 'border-secondary'}`}>
                                            <div className="card-header py-2 d-flex justify-content-between align-items-center">
                                                <strong>{modelo.nome}</strong>
                                                {emUso && (
                                                    <span className="badge badge-success">Em uso</span>
                                                )}
                                            </div>
                                            <div className="card-body p-2">
                                                <div className="row text-center mb-2">
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Bilhetes</small>
                                                        <strong>{(modelo.quantidade ?? 0).toLocaleString('pt-BR')}</strong>
                                                    </div>
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Valor</small>
                                                        <strong>{formatMoney(modelo.valor)}</strong>
                                                    </div>
                                                </div>

                                                {modelo.faixas && modelo.faixas.length > 0 && (
                                                    <table className="table table-sm table-bordered mb-3" style={{ fontSize: '0.78rem' }}>
                                                        <thead className="thead-light">
                                                            <tr>
                                                                <th>Faixa</th>
                                                                <th className="text-right">Qtd</th>
                                                                <th className="text-right">Prêmio</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {modelo.faixas.map(f => (
                                                                <tr key={f.id}>
                                                                    <td>{f.nome}</td>
                                                                    <td className="text-right">{f.quantidade}</td>
                                                                    <td className="text-right">{formatMoney(f.valor_premio)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}

                                                <PermissionGate permissions={['Criar raspadinha']}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-success w-100"
                                                        disabled={criando === modelo.id || emUso || temAtiva}
                                                        onClick={() => criar(modelo.id)}
                                                        title={temAtiva && !emUso ? `Já existe uma raspadinha ativa de ${grupo.label}` : ''}
                                                    >
                                                        {criando === modelo.id ? 'Criando...' : emUso ? 'Em uso' : 'Criar'}
                                                    </button>
                                                </PermissionGate>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Lista de raspadinhas da banca */}
            <div className="conteudo-principal p-0">
                <div className="p-3 border-bottom">
                    <strong>Minhas Raspadinhas</strong>
                </div>
                <table className="table table-sm text-center border mb-0">
                    <thead className="thead-light">
                        <tr>
                            <th>Modelo</th>
                            <th>Valor</th>
                            <th>Vendidos</th>
                            <th>Arrecadado</th>
                            <th>Prêmios pagos</th>
                            <th>Líquido</th>
                            <th>Situação</th>
                            <th>Criada em</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {raspadinhas.length === 0 ? (
                            <tr>
                                <td colSpan="9" className="text-muted p-4">Nenhuma raspadinha criada ainda.</td>
                            </tr>
                        ) : raspadinhas.map(r => (
                            <tr key={r.id}>
                                <td>{r.nome}</td>
                                <td>{formatMoney(r.valor)}</td>
                                <td>{(r.total_gerados ?? 0).toLocaleString('pt-BR')} / {(r.quantidade ?? 0).toLocaleString('pt-BR')}</td>
                                <td>{formatMoney(r.total_arrecadado ?? 0)}</td>
                                <td className="text-danger">{formatMoney(r.total_premios ?? 0)}</td>
                                <td className="text-success">{formatMoney((r.total_arrecadado ?? 0) - (r.total_premios ?? 0))}</td>
                                <td>
                                    <span className={`badge badge-${
                                        r.situacao === 'ativa' ? 'success' :
                                        r.situacao === 'encerrada' ? 'primary' : 'secondary'
                                    }`}>
                                        {r.situacao}
                                    </span>
                                </td>
                                <td>{new Date(r.created_at).toLocaleDateString('pt-BR')}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-info mr-1"
                                        onClick={() => abrirBilhetes(r)}
                                    >
                                        Ver bilhetes
                                    </button>
                                    {r.situacao === 'ativa' && (
                                        <PermissionGate permissions={['Cancelar raspadinha']}>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                disabled={cancelando === r.id}
                                                onClick={() => cancelar(r.id)}
                                            >
                                                {cancelando === r.id ? '...' : 'Cancelar'}
                                            </button>
                                        </PermissionGate>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
